import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChild,
    ViewChildren
} from '@angular/core';

import { TranslatePipe } from '@ngx-translate/core';

import { keyCode } from '@ridango/core/lib/enums/keyboard.enum';

import momenttz from 'moment-timezone';
import { take } from 'rxjs/operators';

import { CalendarControlsComponent } from '../calendar/calendar-controls/calendar-controls.component';
import { CalendarDayComponent } from '../calendar/calendar-day/calendar-day.component';

export class CalendarDay {
    name: number;
    disabled: boolean;
    full_date: Date;
    constructor(short_name, disabled, full_date) {
        this.name = short_name;
        this.disabled = disabled;
        this.full_date = full_date;
    }
}

@Component({
    selector: 'ridango-datepicker',
    templateUrl: './datepicker.component.html',
    styleUrls: ['./datepicker.component.scss'],
    providers: [TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatepickerComponent implements OnInit, OnChanges {
    @Input() visible = false;

    @Input() label = null;

    @Input() date: Date;

    @Input() yearsControl = true;

    @Input() monthsControl = true;

    @Input() minDate: Date = null;

    @Input() maxDate: Date = null;

    @Input() isOpened = false;

    @Input() lang = 'en';

    @Input() disabled = false;

    @Input() dateFormat = 'yyyy-MM-dd';

    @Input() showMobileArrows = false;

    @Input() ariaLabel = 'DATE_PICKER_CALENDAR_LABEL';

    @Input() correctSelectedDate = true;

    @Input() useMaterialIcon = false;

    @Output() dateUpdated = new EventEmitter();

    months = [
        'LOCAL_CALENDAR_JANUARY_MMMM',
        'LOCAL_CALENDAR_FEBRUARY_MMMM',
        'LOCAL_CALENDAR_MARCH_MMMM',
        'LOCAL_CALENDAR_APRIL_MMMM',
        'LOCAL_CALENDAR_MAY_MMMM',
        'LOCAL_CALENDAR_JUNE_MMMM',
        'LOCAL_CALENDAR_JULY_MMMM',
        'LOCAL_CALENDAR_AUGUST_MMMM',
        'LOCAL_CALENDAR_SEPTEMBER_MMMM',
        'LOCAL_CALENDAR_OCTOBER_MMMM',
        'LOCAL_CALENDAR_NOVEMBER_MMMM',
        'LOCAL_CALENDAR_DECEMBER_MMMM'
    ];

    dayNames = [
        'LOCAL_CALENDAR_MONDAY_d',
        'LOCAL_CALENDAR_TUESDAY_d',
        'LOCAL_CALENDAR_WEDNESDAY_d',
        'LOCAL_CALENDAR_THURSDAY_d',
        'LOCAL_CALENDAR_FRIDAY_d',
        'LOCAL_CALENDAR_SATURDAY_d',
        'LOCAL_CALENDAR_SUNDAY_d'
    ];

    days: CalendarDay[];
    weeks: any[] = [];
    selectedDate: Date;
    currentDate: Date;
    screenReaderCurrentMonth: string;
    mobileBackArrowDisabled = true;
    mobileForwardArrowDisabled = false;

    @ViewChild('dateSelectorInput') dateSelectorInput: ElementRef;
    @ViewChild(CalendarControlsComponent) calendarControls: CalendarControlsComponent;
    @ViewChildren(CalendarDayComponent) calendarDays!: QueryList<CalendarDayComponent>;

    constructor(
        public eRef: ElementRef,
        private translatePipe: TranslatePipe
    ) {}

    @HostListener('document:click', ['$event']) onClickOut(event) {
        if (!this.eRef.nativeElement.contains(event.target)) {
            this.isOpened = false;
        }
    }

    ngOnInit() {
        this.minDate = momenttz.tz(this.minDate, 'Europe/Tallinn').toDate();
        this.maxDate = momenttz.tz(this.maxDate, 'Europe/Tallinn').toDate();

        this.currentDate = new Date();
        this.currentDate.setHours(0);
        this.currentDate.setMinutes(0);
        this.currentDate.setSeconds(0);
        this.currentDate.setMilliseconds(0);

        this.date.setHours(0);
        this.date.setMinutes(0);
        this.date.setSeconds(0);
        this.date.setMilliseconds(0);

        if (this.minDate) {
            this.minDate.setHours(0);
            this.minDate.setMinutes(0);
            this.minDate.setSeconds(0);
            this.minDate.setMilliseconds(0);
        }
        if (this.maxDate) {
            this.maxDate.setHours(0);
            this.maxDate.setMinutes(0);
            this.maxDate.setSeconds(0);
            this.maxDate.setMilliseconds(0);
        }

        this.selectedDate = new Date(this.date);
        if (
            this.correctSelectedDate &&
            this.minDate &&
            this.selectedDate &&
            this.selectedDate.getTime() < this.minDate.getTime()
        ) {
            this.selectedDate = new Date(this.minDate);
            this.selectedDate.setDate(this.selectedDate.getDate());
            this.selectedDate.setHours(0);
            this.selectedDate.setMinutes(0);
            this.selectedDate.setSeconds(0);
            this.selectedDate.setMilliseconds(0);
        }
        if (
            this.correctSelectedDate &&
            this.maxDate &&
            this.selectedDate &&
            this.selectedDate.getTime() > this.maxDate.getTime()
        ) {
            this.selectedDate = new Date(this.maxDate);
            this.selectedDate.setDate(this.selectedDate.getDate());
            this.selectedDate.setHours(0);
            this.selectedDate.setMinutes(0);
            this.selectedDate.setSeconds(0);
            this.selectedDate.setMilliseconds(0);
        }
        this.setCalendar();
        this.currentDate = this.selectedDate;
    }

    setCalendar(date = null): void {
        this.days = [];
        const change = date ? date : this.date;
        const firstDay = new Date(change.getFullYear(), change.getMonth(), 1);
        let emptyFromStart = firstDay.getDay();
        if (emptyFromStart === 0) {
            emptyFromStart = 7;
        }
        const lastDay = new Date(change.getFullYear(), change.getMonth() + 1, 0);
        for (let i = 1; i < emptyFromStart; i++) {
            const d = new CalendarDay(null, true, null);
            this.days.push(d);
        }
        for (let i = 1; i <= lastDay.getDate(); i++) {
            const d = new CalendarDay(i, false, new Date(change.getFullYear(), change.getMonth(), i));
            d.disabled = this.isDisabled(d.full_date);
            this.days.push(d);
        }
        while (this.days.length < 42) {
            const d = new CalendarDay(null, true, null);
            this.days.push(d);
        }
        let week = [];
        this.weeks = [];
        this.days.forEach((d: CalendarDay) => {
            week.push(d);
            if (week.length === 7) {
                this.weeks.push(week);
                week = [];
            }
        });
        this.weeks.push(week);

        if (this.minDate) {
            this.mobileBackArrowDisabled = momenttz.tz(change, 'Europe/Tallinn').isSameOrBefore(this.minDate);
        }
        if (this.maxDate) {
            this.mobileForwardArrowDisabled = momenttz.tz(change, 'Europe/Tallinn').isSameOrAfter(this.maxDate);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['date'] && changes['date'].currentValue) {
            this.date = new Date(changes['date'].currentValue);
            this.date.setHours(0, 0, 0, 0);
            this.selectedDate = new Date(this.date);
        }
        if (changes['minDate'] && changes['minDate'].currentValue) {
            this.minDate = new Date(changes['minDate'].currentValue);
            this.minDate.setHours(0, 0, 0, 0);
        }
        if (changes['maxDate'] && changes['maxDate'].currentValue) {
            this.maxDate = new Date(changes['maxDate'].currentValue);
            this.maxDate.setHours(0, 0, 0, 0);
        }
        if (this.date && (changes['date'] || changes['minDate'] || changes['maxDate'])) {
            this.setCalendar();
        }
    }

    dateChanged(date: Date, annouce = false) {
        this.date = date;
        this.setCalendar(date);

        if (annouce) {
            this.screenReaderCurrentMonth = this.translatePipe.transform(this.months[date.getMonth()]);
            setTimeout(() => (this.screenReaderCurrentMonth = ''), 1000);
        }
    }

    isSelected(d: CalendarDay): boolean {
        if (d.full_date) {
            return d.full_date.getTime() === this.selectedDate.getTime();
        }
        return false;
    }

    toHumanReadableDate(date: Date): string {
        if (date) {
            const fullDayNames = [
                'LOCAL_CALENDAR_MONDAY_dddd',
                'LOCAL_CALENDAR_TUESDAY_dddd',
                'LOCAL_CALENDAR_WEDNESDAY_dddd',
                'LOCAL_CALENDAR_THURSDAY_dddd',
                'LOCAL_CALENDAR_FRIDAY_dddd',
                'LOCAL_CALENDAR_SATURDAY_dddd',
                'LOCAL_CALENDAR_SUNDAY_dddd'
            ];

            return `${this.translatePipe.transform(
                fullDayNames[date.getUTCDay()]
            )} ${date.getDate()} ${this.translatePipe.transform(this.months[date.getMonth()])} ${date.getFullYear()}`;
        }

        return '';
    }

    select(d: CalendarDay): void {
        this.selectedDate = new Date(d.full_date);
        this.dateUpdated.emit(this.selectedDate);
        this.isOpened = false;
    }

    showCalendar(event?: KeyboardEvent) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        if (!this.disabled) {
            this.isOpened = true;
            let calendarDay = this.calendarDays.find((day) => day.selected);
            if (!calendarDay) {
                calendarDay = this.calendarDays.find((day) => day.item.name === 1);
            }
            setTimeout(() => calendarDay.setFocus());
        }
    }

    moveToNextDate(eventData) {
        const { event, calendarDay } = eventData;

        const day = new Date(calendarDay.full_date);

        let flag = false;

        switch (event.keyCode) {
            case keyCode.ENTER:
            case keyCode.SPACE:
                this.select(calendarDay);
                this.dateSelectorInput.nativeElement.focus();
                flag = true;
                break;
            case keyCode.ESCAPE:
                this.isOpened = false;
                this.dateSelectorInput.nativeElement.focus();
                break;
            case keyCode.TAB:
                if (event.shiftKey) {
                    this.calendarControls.setFocus(true);
                } else {
                    this.calendarControls.setFocus();
                }
                flag = true;
                break;
            case keyCode.ARROW_DOWN:
                day.setDate(day.getDate() + 7);
                this.moveFocusToDay(day);
                flag = true;
                break;
            case keyCode.ARROW_UP:
                day.setDate(day.getDate() - 7);
                this.moveFocusToDay(day);
                flag = true;
                break;
            case keyCode.ARROW_RIGHT:
                day.setDate(day.getDate() + 1);
                this.moveFocusToDay(day);
                flag = true;
                break;
            case keyCode.ARROW_LEFT:
                day.setDate(day.getDate() - 1);
                this.moveFocusToDay(day);
                flag = true;
                break;
        }

        if (flag) {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    handleControlsKeyDown(event: keyCode) {
        switch (event) {
            case keyCode.TAB:
                this.returnToFocusedDate();
                break;
            case keyCode.ESCAPE:
                this.isOpened = false;
                this.dateSelectorInput.nativeElement.focus();
                break;
        }
    }

    private returnToFocusedDate(): void {
        const nextDayComponent = this.calendarDays.find(
            (d: CalendarDayComponent) => d.item.name === this.currentDate.getDate()
        );
        nextDayComponent.setFocus();
    }

    private moveFocusToDay(day: Date): void {
        if (!this.isDisabled(day)) {
            let nextDayComponent = this.calendarDays.find((d) => d.item.name === day.getDate());
            // check if nextDayComponent is undefined e.g. months without 31 days can result in undefined
            if (!nextDayComponent || nextDayComponent.item.full_date.getMonth() !== day.getMonth()) {
                this.dateChanged(day);
                this.calendarDays.changes.pipe(take(1)).subscribe((days) => {
                    nextDayComponent = days.find((d: CalendarDayComponent) => d.item.name === day.getDate());
                    nextDayComponent.setFocus();
                });
            } else {
                nextDayComponent.setFocus();
            }
            this.currentDate = day;
        }
    }

    private isDisabled(date: Date): boolean {
        let disabled = false;
        if (this.minDate && date.getTime() < this.minDate.getTime()) {
            disabled = true;
        }
        if (this.maxDate && date.getTime() > this.maxDate.getTime()) {
            disabled = true;
        }

        return disabled;
    }

    onDayArrowClick(direction: string): any {
        const date: any = momenttz.tz(this.date, 'Europe/Tallinn');
        if (direction === 'back') {
            date.subtract(1, 'days');
        } else if (direction === 'forward') {
            date.add(1, 'days');
        }
        if (date.isBetween(this.minDate, this.maxDate, 'day', '[]')) {
            const d = new CalendarDay(date.day(), false, date.toDate());
            this.select(d);
            this.dateChanged(date.toDate());
        }
    }
}
